import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Candle } from "@/components/Candle";
import { Progress } from "@/components/ui/progress";
import { api, euros } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Flame, Plus, Loader2, ExternalLink, CreditCard, Infinity as InfinityIcon, Camera, Users, BellRing, ChevronRight, Gift } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PlanSelector } from "@/components/PlanSelector";
import { ringStyle } from "@/lib/flagRing";
import { FlagBadge } from "@/components/FlagBadge";
import { PhotoCropDialog } from "@/components/PhotoCropDialog";
import { useTranslation } from "react-i18next";
import { useCountdown, formatRemaining } from "@/hooks/useCountdown";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const fmtDMY = (d) => (d ? new Date(d + "T00:00:00").toLocaleDateString("fr-FR") : "");

const LiveFlameCounter = ({ candle }) => {
  const { remaining, expired } = useCountdown(candle.expires_at, candle.burn_hours);
  if (expired) return null;
  return (
    <div className="flex items-center gap-1.5 rounded-full border border-amber-300/30 bg-amber-300/5 px-2.5 py-1 text-xs font-medium tabular-nums text-amber-300 shadow-[0_0_14px_-4px_rgba(251,191,36,0.5)]" data-testid="my-candle-countdown">
      <Flame size={12} className="animate-pulse text-amber-300" />
      {formatRemaining(remaining)}
    </div>
  );
};

export default function MyCandles() {
  const { user, loading, setUser } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [candles, setCandles] = useState([]);
  const [followedGroups, setFollowedGroups] = useState([]);
  const [myGroups, setMyGroups] = useState([]);
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogCandle, setDialogCandle] = useState(null);
  const [dialogType, setDialogType] = useState("light");
  const [selectedPlan, setSelectedPlan] = useState("decouverte");
  const [extraPlans, setExtraPlans] = useState([]);
  const [morePlansOpen, setMorePlansOpen] = useState(false);
  const [morePlan, setMorePlan] = useState("1mois");
  const photoFileRef = useRef();
  const [photoCandleId, setPhotoCandleId] = useState(null);
  const [photoBusyId, setPhotoBusyId] = useState(null);
  const [cropSrc, setCropSrc] = useState(null);

  const pickPhoto = (candleId) => {
    setPhotoCandleId(candleId);
    photoFileRef.current?.click();
  };

  const onPhotoFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !photoCandleId) return;
    if (file.size > 40 * 1024 * 1024) return toast.error(t("create.photoTooBig"));
    const reader = new FileReader();
    reader.onload = () => setCropSrc(reader.result);
    reader.readAsDataURL(file);
  };

  const applyCroppedPhoto = async (b64) => {
    const id = photoCandleId;
    setCropSrc(null);
    if (!id) return;
    setPhotoBusyId(id);
    try {
      await api.patch(`/candles/${id}`, { photo_base64: b64 });
      toast.success(t("mycandles.updated"));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("memorial.error"));
    } finally { setPhotoBusyId(null); }
  };

  const load = useCallback(async () => {
    try {
      const [res, fg, mg] = await Promise.all([
        api.get("/me/candles"),
        api.get("/me/followed-groups").catch(() => ({ data: [] })),
        api.get("/me/groups").catch(() => ({ data: [] })),
      ]);
      setCandles(res.data);
      setFollowedGroups(fg.data || []);
      setMyGroups(mg.data || []);
    } finally {
      setFetching(false);
    }
  }, []);

  useEffect(() => {
    if (!loading && !user) navigate("/");
    if (user) load();
  }, [loading, user, navigate, load]);

  useEffect(() => {
    api.get("/settings").then((r) => {
      setPlans(r.data.plans || []);
      if (r.data.extra_plans?.length) {
        setExtraPlans(r.data.extra_plans);
        setMorePlan(r.data.extra_plans[0].code);
      }
    }).catch(() => {});
  }, []);

  const openPay = (candle, type) => {
    setDialogCandle(candle);
    setDialogType(type);
    setSelectedPlan("decouverte");
    setDialogOpen(true);
  };

  const submitPay = async (type, plan) => {
    if (!dialogCandle) return;
    setBusy(true);
    try {
      const endpoint = type === "light" ? `/candles/${dialogCandle.id}/pay` : `/candles/${dialogCandle.id}/relight`;
      const res = await api.post(endpoint, { origin_url: window.location.origin, plan });
      if (res.data.mode === "stripe" && res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.success(t("mycandles.updated"));
        setDialogOpen(false);
        setMorePlansOpen(false);
        load();
        setBusy(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t("memorial.error"));
      setBusy(false);
    }
  };

  const confirmPay = () => submitPay(dialogType, selectedPlan);
  const confirmMore = () => submitPay("relight", morePlan);

  const credit = user?.free_months_credit || 0;
  const eternalCredit = user?.free_eternal_candles || 0;
  const applyFreeMonth = async (candle, e) => {
    e.stopPropagation();
    try {
      const res = await api.post(`/me/candles/${candle.id}/use-free-month`);
      toast.success(t("referral.monthApplied"));
      if (user) setUser({ ...user, free_months_credit: res.data.free_months_credit });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("memorial.error"));
    }
  };
  const applyFreeEternal = async (candle, e) => {
    e.stopPropagation();
    try {
      const res = await api.post(`/me/candles/${candle.id}/use-free-eternal`);
      toast.success(t("ambassador.litToast"));
      if (user) setUser({ ...user, free_eternal_candles: res.data.free_eternal_candles });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("memorial.error"));
    }
  };

  const isAmb = !!user?.is_ambassador;
  const hideFinance = isAmb && eternalCredit > 0;

  return (
    <div className="min-h-[100dvh]">
      <Header />
      <input ref={photoFileRef} type="file" accept="image/*" className="hidden" onChange={onPhotoFile} data-testid="my-candle-photo-input" />
      <PhotoCropDialog open={!!cropSrc} image={cropSrc} onCancel={() => setCropSrc(null)} onCropped={applyCroppedPhoto} />
      <div className="mx-auto max-w-3xl px-5 py-8 pb-28 text-center">
        <div className="mb-6">
          <h1 className="text-center text-2xl font-medium">{t("mycandles.title")}</h1>
          <div className="mt-4 flex items-center justify-center gap-3">
            <Button variant="outline" className="flex-1 rounded-full border-amber-300/40 text-amber-200 hover:bg-amber-300/10" onClick={() => navigate("/souvenirs")} data-testid="family-memories-btn">
              <Users size={16} className="mr-1.5" /> {myGroups.length > 0 ? t("mycandles.groupShort") : t("family.newGroup")}
            </Button>
            {!hideFinance && (
              <Button className="flex-1 rounded-full" onClick={() => navigate("/creer")} data-testid="new-candle-btn">
                <Plus size={16} className="mr-1.5" /> {t("mycandles.newFull")}
              </Button>
            )}
          </div>
          {!hideFinance && (
          <button type="button" onClick={() => navigate("/parrainage")} className="mt-3 flex w-full items-center gap-2 rounded-full border border-amber-300/40 bg-amber-300/[0.06] px-4 py-2.5 text-sm text-amber-200 transition-colors hover:bg-amber-300/10" data-testid="referral-entry-btn">
            <Gift size={16} className="shrink-0" />
            <span className="flex-1 text-left font-medium">{credit > 0 ? t("referral.creditBanner", { count: credit }) : t("referral.entry")}</span>
            <ChevronRight size={16} className="shrink-0 opacity-70" />
          </button>
          )}
          {eternalCredit > 0 && (
            <button type="button" onClick={() => navigate("/creer")} className="mt-3 flex w-full items-center justify-center gap-2 rounded-2xl border border-amber-400/30 bg-black px-4 py-2.5 text-sm text-amber-200 transition-colors hover:border-amber-400/60" data-testid="eternal-credit-banner">
              <InfinityIcon size={16} className="shrink-0" />
              <span className="font-medium">{t("ambassador.creditBanner", { count: eternalCredit })}</span>
            </button>
          )}
        </div>

        {fetching ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin text-sky-400" /></div>
        ) : candles.length === 0 ? (
          hideFinance ? null : (
            <div className="rounded-3xl border border-white/10 bg-card/40 p-10 text-center">
              <Flame className="mx-auto mb-3 text-amber-300" />
              <p className="text-muted-foreground">{t("mycandles.empty")}</p>
              <Button className="mt-4 rounded-full" onClick={() => navigate("/creer")}>{t("mycandles.lightCandle")}</Button>
            </div>
          )
        ) : (
          <div className="space-y-4">
            {candles.map((c) => (
              <div key={c.id} onClick={() => navigate(`/candle/${c.id}`)} className="cursor-pointer rounded-2xl border border-white/10 bg-card/40 p-5 transition-colors fade-in hover:border-white/25" data-testid="my-candle-item">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex min-w-0 items-center gap-3">
                    <div className={`relative shrink-0 rounded-full ${ringStyle(c.flag_colors) ? "p-[3px]" : ""}`} style={ringStyle(c.flag_colors) || undefined} title={c.country_name || ""} data-testid="my-candle-ring">
                    <button type="button" onClick={(e) => { e.stopPropagation(); pickPhoto(c.id); }} className={`group relative h-12 w-12 overflow-hidden rounded-full ${ringStyle(c.flag_colors) ? "border-2 border-black/50" : "border-2 border-amber-300/80 shadow-[0_0_14px_rgba(251,191,36,0.3)]"}`} title={t("mycandles.changePhoto")} data-testid="my-candle-photo-btn">
                      <img src={c.photo_base64 || "/candle.png"} alt={c.deceased_name} className="h-full w-full object-cover" data-testid="my-candle-photo" />
                      <span className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 transition-opacity group-hover:opacity-100">
                        {photoBusyId === c.id ? <Loader2 size={16} className="animate-spin text-white" /> : <Camera size={16} className="text-white" />}
                      </span>
                    </button>
                    <FlagBadge flag={c.flag} name={c.country_name} />
                    </div>
                    <div className="min-w-0">
                      <h3 className="truncate text-lg font-medium">{c.deceased_name}</h3>
                      {(c.birth_date || c.death_date) && (
                        <p className="mt-0.5 text-sm text-amber-200/80" data-testid="my-candle-dates">
                          {fmtDMY(c.birth_date)}{c.birth_date && c.death_date ? " – " : ""}{fmtDMY(c.death_date)}
                        </p>
                      )}
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {c.paid ? (c.eternal ? t("mycandles.eternal") : (c.is_expired ? t("mycandles.extinguished") : t("mycandles.active"))) : t("mycandles.pending")} · {t("mycandles.messagesCount")} : {c.condolence_count || 0}
                      </p>
                    </div>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-2">
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" className="rounded-full border-white/15" onClick={(e) => { e.stopPropagation(); navigate(`/candle/${c.id}`); }} data-testid="view-candle-btn">
                        <ExternalLink size={14} />
                      </Button>
                      {!c.paid ? (
                        !hideFinance && (
                          <Button size="sm" className="rounded-full" disabled={busy} onClick={(e) => { e.stopPropagation(); openPay(c, "light"); }} data-testid="finish-payment-btn">
                            <CreditCard size={14} className="mr-1" /> {t("mycandles.pay")}
                          </Button>
                        )
                      ) : c.eternal ? (
                        <span className="flex items-center gap-1 rounded-full border border-sky-400/30 bg-sky-400/10 px-3 py-1.5 text-xs text-sky-300" data-testid="eternal-badge">
                          <InfinityIcon size={13} /> {t("mycandles.eternal")}
                        </span>
                      ) : (
                        !hideFinance && (
                          <Button size="sm" className="rounded-full" disabled={busy} onClick={(e) => { e.stopPropagation(); openPay(c, "relight"); }} data-testid="relight-mine-btn">
                            <Flame size={14} className="mr-1" /> {t("mycandles.revive")}
                          </Button>
                        )
                      )}
                    </div>
                    {c.paid && !c.eternal && <LiveFlameCounter candle={c} />}
                    {credit > 0 && !c.eternal && (
                      <Button size="sm" variant="outline" className="rounded-full border-amber-300/50 text-amber-200 hover:bg-amber-300/10" onClick={(e) => applyFreeMonth(c, e)} data-testid="use-free-month-btn">
                        <Gift size={13} className="mr-1" /> {t("referral.useCredit")}
                      </Button>
                    )}
                    {eternalCredit > 0 && !c.eternal && (
                      <Button size="sm" variant="outline" className="rounded-full border-sky-400/50 text-sky-300 hover:bg-sky-400/10" onClick={(e) => applyFreeEternal(c, e)} data-testid="use-free-eternal-btn">
                        <InfinityIcon size={13} className="mr-1" /> {t("ambassador.createButton")}
                      </Button>
                    )}
                  </div>
                </div>
                {c.paid && (
                  <div className="mt-3">
                    <Progress value={c.progress * 100} className="h-2" />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {eternalCredit > 0 && (
          <button
            type="button"
            onClick={() => navigate("/creer")}
            className="group relative mt-8 block w-full rounded-3xl border border-amber-400/20 bg-black p-5 text-center transition-all hover:border-amber-400/60 sm:p-6"
            data-testid="ambassador-slots"
          >
            <span
              className="absolute bottom-4 right-4 flex h-11 w-11 items-center justify-center rounded-full border-2 border-amber-400/70 bg-black text-lg font-bold text-amber-300 shadow-[0_0_14px_rgba(251,191,36,0.35)]"
              data-testid="ambassador-slot-count"
            >
              {eternalCredit}
            </span>
            <h2 className="flex items-center justify-center gap-2 text-base font-semibold text-amber-300">
              <InfinityIcon size={18} /> {t("ambassador.slotsTitle")}
            </h2>
            <p className="mx-auto mt-1.5 max-w-md text-sm text-white/80">
              {t("ambassador.giftText")}
            </p>
            <div className="mx-auto mt-4 flex justify-center transition-transform group-hover:scale-[1.03]" data-testid="ambassador-slot">
              <Candle extinguished size={200} />
            </div>
            <span className="mt-2 block text-center text-sm font-medium tracking-wide text-amber-200/90" data-testid="ambassador-slot-label">
              {t("ambassador.slotLabel", { count: eternalCredit })}
            </span>
          </button>
        )}


        {!fetching && myGroups.length > 0 && (
          <section className="mt-8" data-testid="my-groups-section">
            <h2 className="mb-3 flex items-center gap-2 text-base font-medium text-amber-200">
              <Users size={17} /> {t("mycandles.groupShort")}
            </h2>
            <div className="space-y-4">
              {myGroups.map((g) => (
                <div key={g.id} onClick={() => navigate(`/souvenirs/${g.id}`)} className="cursor-pointer rounded-2xl border border-amber-300/20 bg-amber-300/[0.04] p-5 transition-colors fade-in hover:border-amber-300/50" data-testid="my-group-item">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex min-w-0 items-center gap-3">
                      {g.cover_url ? (
                        <img src={`${BACKEND}${g.cover_url}`} alt={g.name} className="h-12 w-12 shrink-0 rounded-xl border-2 border-amber-300/60 object-cover" data-testid="my-group-cover" />
                      ) : (
                        <div className="flex -space-x-3">
                          {g.candles.slice(0, 3).map((c) => (
                            <div key={c.id} className={`shrink-0 rounded-full ${ringStyle(c.flag_colors) ? "p-[2px]" : ""}`} style={ringStyle(c.flag_colors) || undefined} title={c.country_name || ""}>
                              <img src={`${BACKEND}${c.photo_url}`} alt={c.deceased_name} className={`h-11 w-11 rounded-full object-cover ${ringStyle(c.flag_colors) ? "border border-black/50" : "border-2 border-amber-300/70"}`} />
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="min-w-0">
                        <h3 className="truncate text-lg font-medium">{g.name}</h3>
                        <p className="mt-0.5 truncate text-sm text-amber-200/80">
                          {g.candles.map((c) => c.deceased_name).join(", ")}
                        </p>
                        <p className="mt-0.5 text-xs text-muted-foreground">{t("family.candlesCount", { count: g.candle_count })}</p>
                      </div>
                    </div>
                    <ChevronRight size={18} className="shrink-0 text-muted-foreground" />
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {!fetching && followedGroups.length > 0 && (
          <section className="mt-8" data-testid="followed-groups-section">
            <h2 className="mb-3 flex items-center gap-2 text-base font-medium text-sky-300">
              <BellRing size={17} /> {t("family.followedGroups")}
            </h2>
            <div className="space-y-4">
              {followedGroups.map((g) => (
                <div key={g.id} onClick={() => navigate(`/famille/${g.share_token}`)} className="cursor-pointer rounded-2xl border border-sky-400/20 bg-sky-400/[0.04] p-5 transition-colors fade-in hover:border-sky-400/40" data-testid="followed-group-item">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex min-w-0 items-center gap-3">
                      {g.cover_url ? (
                        <img src={`${BACKEND}${g.cover_url}`} alt={g.name} className="h-12 w-12 shrink-0 rounded-xl border-2 border-sky-400/60 object-cover" data-testid="followed-group-cover" />
                      ) : (
                        <div className="flex -space-x-3">
                          {g.candles.slice(0, 3).map((c) => (
                            <div key={c.id} className={`shrink-0 rounded-full ${ringStyle(c.flag_colors) ? "p-[2px]" : ""}`} style={ringStyle(c.flag_colors) || undefined} title={c.country_name || ""}>
                              <img src={`${BACKEND}${c.photo_url}`} alt={c.deceased_name} className={`h-11 w-11 rounded-full object-cover ${ringStyle(c.flag_colors) ? "border border-black/50" : "border-2 border-amber-300/70"}`} />
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="min-w-0">
                        <h3 className="truncate text-lg font-medium">{g.name}</h3>
                        <p className="mt-0.5 truncate text-sm text-amber-200/80">
                          {g.candles.map((c) => c.deceased_name).join(", ")}
                        </p>
                        <p className="mt-0.5 text-xs text-muted-foreground">{t("family.candlesCount", { count: g.candle_count })}</p>
                      </div>
                    </div>
                    <div className="flex shrink-0 flex-col items-end gap-2">
                      <span className="flex items-center gap-1.5 rounded-full border border-emerald-400/40 bg-emerald-400/10 px-3 py-1.5 text-xs font-medium text-emerald-300" data-testid="followed-badge">
                        <BellRing size={13} /> {t("family.following")}
                      </span>
                      <ChevronRight size={18} className="text-muted-foreground" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="glass border-white/10 sm:max-w-md" data-testid="mycandles-plan-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-medium">{dialogType === "light" ? t("mycandles.dialogLightTitle") : t("mycandles.dialogRelightTitle")}</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">{dialogCandle ? t("mycandles.chooseDurationFor", { name: dialogCandle.deceased_name }) : t("mycandles.chooseDuration") + "."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-1">
            <PlanSelector plans={plans} value={selectedPlan} onChange={setSelectedPlan} />
            <Button onClick={confirmPay} disabled={busy} className="w-full rounded-full" data-testid="mycandles-plan-confirm-btn">
              {busy ? <Loader2 className="animate-spin" size={16} /> : <><Flame size={16} className="mr-2" /> {dialogType === "light" ? t("mycandles.light") : t("mycandles.prolong")} ({euros(plans.find((p) => p.code === selectedPlan)?.price_cents ?? 100)})</>}
            </Button>
            {dialogType === "relight" && extraPlans.length > 0 && (
              <Button onClick={() => { setDialogOpen(false); setMorePlansOpen(true); }} disabled={busy} className="w-full rounded-full border border-sky-400 bg-sky-400 font-semibold text-black hover:bg-sky-300" data-testid="mycandles-more-plans-btn">
                <InfinityIcon size={16} className="mr-2" /> {t("memorial.extendMore")}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Prolonger Plus : durées supplémentaires */}
      <Dialog open={morePlansOpen} onOpenChange={setMorePlansOpen}>
        <DialogContent className="glass border-white/10 sm:max-w-md" data-testid="mycandles-more-plans-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-medium">{t("memorial.extendMore")}</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">{dialogCandle ? t("mycandles.chooseDurationFor", { name: dialogCandle.deceased_name }) : t("mycandles.chooseDuration") + "."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-1">
            <PlanSelector plans={extraPlans} value={morePlan} onChange={setMorePlan} />
            <Button onClick={confirmMore} disabled={busy} className="w-full rounded-full" data-testid="mycandles-more-plans-confirm-btn">
              {busy ? <Loader2 className="animate-spin" size={16} /> : <><Flame size={16} className="mr-2" /> {t("mycandles.prolong")} ({euros(extraPlans.find((p) => p.code === morePlan)?.price_cents ?? 0)})</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}
